/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useCallback, useEffect, useMemo, useState } from "react";
import { CosmosTable as Table, CosmosText, ScrollContainer, CosmosButtonProps, ButtonList } from "@components/elements";
import { EventsTableRowFragment, EventsTableSectionFragment } from "@cosmos/hygraph-schema";
import { css } from "@panda/css";
import { Property } from "@panda/types/csstype";
import { VStack } from "@panda/jsx";
import { safeGetTime } from "@components/utils";
import { EventsTableRow } from "./EventsTableRow.molecule";

export const EventsTable = ({
	upcomingTabLabel,
	pastTabLabel,
	titleColumnLabel,
	locationColumnLabel,
	detailsColumnLabel,
	events = [],
}: EventsTableSectionFragment): React.JSX.Element => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [activeTabRows, setActiveTabRows] = useState<EventsTableRowFragment[]>([]);

	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 60000); // update every minute
		return () => clearInterval(interval);
	}, []);

	const { pastEvents, upcomingEvents } = useMemo(() => {
		const pastEvents: EventsTableRowFragment[] = [];
		const upcomingEvents: EventsTableRowFragment[] = [];

		events.forEach((event) => {
			const eventTime = safeGetTime(event.timeAndDate);
			if (eventTime < now) {
				pastEvents.push(event);
			} else {
				upcomingEvents.push(event);
			}
		});

		return {
			pastEvents: pastEvents.sort((a, b) => safeGetTime(b.timeAndDate) - safeGetTime(a.timeAndDate)), // ASC
			upcomingEvents: upcomingEvents.sort((a, b) => safeGetTime(a.timeAndDate) - safeGetTime(b.timeAndDate)), // DESC
		};
	}, [events, now]);

	useEffect(() => {
		if ((activeIndex > 0 && pastEvents.length) || !upcomingEvents.length) {
			setActiveIndex(1);
			setActiveTabRows(pastEvents);
		} else {
			setActiveIndex(0);
			setActiveTabRows(upcomingEvents);
		}
	}, [activeIndex, setActiveIndex, setActiveTabRows, pastEvents, upcomingEvents]);

	const onButtonClick = useCallback(
		(index: number) => {
			if (typeof index === "number") {
				setActiveIndex(index);
			}
		},
		[setActiveIndex],
	);

	const tabButtons: CosmosButtonProps[] = useMemo(() => {
		const finalUpcomingTabLabel = `${upcomingTabLabel || "Upcoming"} (${upcomingEvents.length})`;
		const finalPastTabLabel = `${pastTabLabel || "Past events"} (${pastEvents.length})`;
		return [0, 1].map((i) => {
			const hasNoEvents = (i === 0 && upcomingEvents.length === 0) || (i === 1 && pastEvents.length === 0);
			return {
				size: "lg",
				label: i === 0 ? finalUpcomingTabLabel : finalPastTabLabel,
				variant: activeIndex === i ? "contrast" : "outline",
				onClick: () => {
					if (hasNoEvents) return;
					onButtonClick(i);
				},
				analytics: {
					eventCategory: "button",
					interactionType: "click",
					eventAction: "select",
					eventLabel: activeIndex === 0 ? finalUpcomingTabLabel : finalPastTabLabel,
					eventValue: {
						component: "EventsTable",
						section: "section",
					},
				},
				className: css({
					pointerEvents: hasNoEvents ? "none" : "unset",
					borderRadius: "eds.borderRadius.full",
					textStyle: "uiMD",
					fontWeight: "bold",
					smDown: {
						borderRadius: "eds.borderRadius.full",
					},
				}),
			};
		});
	}, [upcomingEvents.length, upcomingTabLabel, activeIndex, pastTabLabel, pastEvents.length, onButtonClick]);

	if (!events.some((event: EventsTableRowFragment) => !!event.title || !!event.location)) return <></>;

	const gridTemplateColumns: Property.GridTemplateColumns = "9fr 5fr 20fr";
	return (
		<VStack gap="eds.dimension.32" alignItems="stretch">
			<ButtonList buttons={tabButtons} />
			<ScrollContainer>
				<Table minWidth="879px" gridTemplateColumns={gridTemplateColumns}>
					<Table.Header>
						<Table.Column id="title">
							<CosmosText variant="uiMD" color="eds.text.secondary" text={titleColumnLabel || "Title"} />
						</Table.Column>
						<Table.Column id="location">
							<CosmosText variant="uiMD" color="eds.text.secondary" text={locationColumnLabel || "Location"} />
						</Table.Column>
						<Table.Column id="details">
							<CosmosText variant="uiMD" color="eds.text.secondary" text={detailsColumnLabel || "Detail"} />
						</Table.Column>
					</Table.Header>
					<Table.Body>
						{activeTabRows.map((event: EventsTableRowFragment, i: number) => {
							if (!event.title) return null;
							return <EventsTableRow key={`events-table-row-${i}`} {...event} />;
						})}
					</Table.Body>
				</Table>
			</ScrollContainer>
		</VStack>
	);
};
